import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
  Alert,
  Card,
  CardMedia,
  CardContent,
  Divider,
  Stack,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';

interface PlantIdentificationResultProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  result: {
    success?: boolean;
    identified: boolean;
    plantExists?: boolean;
    placeholderExists?: boolean;
    placeholderInfo?: {
      message: string;
    };
    plant?: any;
    analysis?: {
      identified: boolean;
      confidence: string;
      latin_name: string;
      hungarian_name: string;
      plant_type: string;
      characteristics?: any;
      notes?: string;
    };
    message: string;
    isApiError?: boolean;
    error?: string;
    requestId?: number;
  };
  onAddToList?: (plant: any) => void;
  imageUrl?: string;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
  },
  // Teljes képernyős (mobil) módban ne legyen lekerekítés
  '& .MuiDialog-paperFullScreen': {
    borderRadius: 0,
  },
}));

const ConfidenceChip = styled(Chip)<{ confidence: string }>(({ theme, confidence }) => ({
  backgroundColor:
    confidence === 'high'
      ? theme.palette.success.light
      : confidence === 'medium'
      ? theme.palette.warning.light
      : theme.palette.error.light,
  color: theme.palette.getContrastText(
    confidence === 'high'
      ? theme.palette.success.light
      : confidence === 'medium'
      ? theme.palette.warning.light
      : theme.palette.error.light
  ),
}));

const PlantIdentificationResult: React.FC<PlantIdentificationResultProps> = ({
  open,
  onClose,
  onSuccess,
  result,
  onAddToList,
  imageUrl,
}) => {
  const theme = useTheme();
  // Mobilon teljes képernyős lap (nem szűk felugró modal)
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Null check - ha nincs result, üres objektumot használunk
  if (!result) {
    return (
      <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <LocalFloristIcon color="primary" />
              <Typography variant="h6">Növényazonosítás eredménye</Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error">
            <Typography>Hiba történt az eredmény betöltése során.</Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Bezárás
          </Button>
        </DialogActions>
      </StyledDialog>
    );
  }

  const { identified, plantExists, plant, analysis } = result;

  const getConfidenceLabel = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'Magas bizonyosság';
      case 'medium':
        return 'Közepes bizonyosság';
      case 'low':
        return 'Alacsony bizonyosság';
      default:
        // Magyar értékek kezelése
        if (confidence?.toLowerCase() === 'magas') return 'Magas bizonyosság';
        if (confidence?.toLowerCase() === 'közepes') return 'Közepes bizonyosság';
        if (confidence?.toLowerCase() === 'alacsony') return 'Alacsony bizonyosság';
        return confidence;
    }
  };

  // Confidence érték normalizálása angol formára a stílusozáshoz
  const normalizeConfidence = (confidence: string): string => {
    const map: Record<string, string> = { magas: 'high', közepes: 'medium', alacsony: 'low' };
    return map[confidence?.toLowerCase()] || confidence || 'medium';
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            {result.isApiError ? (
              <>
                <InfoIcon color="warning" />
                <Typography variant="h6">Szolgáltatás hiba</Typography>
              </>
            ) : (
              <>
                <LocalFloristIcon color="primary" />
                <Typography variant="h6">Növényazonosítás eredménye</Typography>
              </>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={3}>
          {(() => {
            // API hiba esetén
            if (!result.success && result.isApiError) {
              return (
                <>
                  <Alert severity="warning" icon={<InfoIcon />}>
                    <Typography variant="h6" gutterBottom>
                      Kép elemző szolgáltatás hiba
                    </Typography>
                    <Typography variant="body2">
                      {result.error || 'A kép elemző szolgáltatás jelenleg nem elérhető.'}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      Ajánlott műveletek:
                    </Typography>
                    <Box component="ul" sx={{ mt: 1, pl: 3 }}>
                      <li>Próbálja újra néhány perc múlva</li>
                      <li>Használjon másik képet</li>
                      <li>Lépjen kapcsolatba az adminisztrátorral, ha a probléma továbbra is fennáll</li>
                    </Box>
                  </Alert>

                  {result.requestId && (
                    <Alert severity="info">
                      <Typography variant="body2">
                        A probléma bejelentésre került az adminisztrátoroknak (Kérés ID: {result.requestId}).
                        Megpróbáljuk megoldani a szolgáltatás elérhetetlenségét.
                      </Typography>
                    </Alert>
                  )}
                </>
              );
            }

            // Sikeres azonosítás esetén
            if (identified && analysis) {
              return (
                <>
                  <Card variant="outlined">
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        {imageUrl && (
                          <Grid item xs={4}>
                            <CardMedia
                              component="img"
                              height="120"
                              image={imageUrl}
                              alt={analysis.hungarian_name}
                              sx={{ borderRadius: 1 }}
                            />
                          </Grid>
                        )}
                        <Grid item xs={imageUrl ? 8 : 12}>
                          <Typography variant="h5" gutterBottom>
                            {analysis.hungarian_name}
                          </Typography>
                          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                            {analysis.latin_name}
                          </Typography>
                          <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                            <ConfidenceChip
                              size="small"
                              confidence={normalizeConfidence(analysis.confidence)}
                              label={getConfidenceLabel(analysis.confidence)}
                            />
                            {analysis.plant_type && (
                              <Chip size="small" label={analysis.plant_type} variant="outlined" />
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Növény jellemzői - dinamikusan megjelenítjük az elérhető mezőket */}
                  {analysis?.characteristics && (() => {
                    const c = analysis.characteristics;
                    // Ismert mezők amiknek szép megjelenítést adunk
                    const knownFields: Array<{ key: string; label: string }> = [
                      { key: 'height', label: 'Magasság' },
                      { key: 'spread', label: 'Szélesség' },
                      { key: 'matureSize', label: 'Kifejlett méret' },
                      { key: 'bloom_time', label: 'Virágzás' },
                      { key: 'bloomTime', label: 'Virágzás' },
                      { key: 'bloom_color', label: 'Virág színe' },
                      { key: 'bloomColor', label: 'Virág színe' },
                      { key: 'color', label: 'Szín' },
                      { key: 'light_requirement', label: 'Fényigény' },
                      { key: 'lightRequirement', label: 'Fényigény' },
                      { key: 'water_requirement', label: 'Vízigény' },
                      { key: 'waterRequirements', label: 'Vízigény' },
                      { key: 'soil_type', label: 'Talaj' },
                      { key: 'soilType', label: 'Talaj' },
                      { key: 'growth_rate', label: 'Növekedési sebesség' },
                      { key: 'growthRate', label: 'Növekedési sebesség' },
                      { key: 'careTips', label: 'Gondozási tippek' },
                    ];
                    // Kihagyandó mezők (már máshol megjelenítjük)
                    const skipKeys = new Set(['hungarianName', 'latinName', 'category', 'confidence', 'description', 'identified']);

                    const displayFields = knownFields.filter(f => c[f.key] && !skipKeys.has(f.key));
                    // Ha van leírás, az elsőként jelenik meg
                    const hasDescription = c.description && typeof c.description === 'string';

                    if (!hasDescription && displayFields.length === 0) return null;

                    return (
                      <>
                        {hasDescription && (
                          <>
                            <Divider />
                            <Alert severity="info" icon={<InfoIcon />}>
                              <Typography variant="body2">
                                {c.description}
                              </Typography>
                            </Alert>
                          </>
                        )}
                        {displayFields.length > 0 && (
                          <>
                            <Divider />
                            <Box>
                              <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                                Növény jellemzői:
                              </Typography>
                              <Grid container spacing={1}>
                                {displayFields.map((field) => (
                                  <Grid item xs={field.key === 'careTips' ? 12 : 6} key={field.key}>
                                    <Typography variant="body2">
                                      <strong>{field.label}:</strong> {String(c[field.key])}
                                    </Typography>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          </>
                        )}
                      </>
                    );
                  })()}

                  {/* Státusz üzenet */}
                  <Alert
                    severity={
                      result.placeholderExists
                        ? 'warning'
                        : plantExists
                        ? 'success'
                        : 'info'
                    }
                    icon={
                      result.placeholderExists
                        ? <InfoIcon />
                        : plantExists
                        ? <AddIcon />
                        : <InfoIcon />
                    }
                  >
                    {result.placeholderExists
                      ? 'Ez a növény korábban elutasításra került és nem érhető el az adatbázisban.'
                      : plantExists
                      ? 'A növény megtalálható az adatbázisban. Hozzáadhatja a listájához.'
                      : result.message ||
                        'A növény azonosítva és hozzáadva a jóváhagyásra váró növényekhez.'}
                  </Alert>

                  {/* Placeholder információ megjelenítése */}
                  {result.placeholderInfo && (
                    <Alert severity="info">
                      <Typography variant="body2">{result.placeholderInfo.message}</Typography>
                    </Alert>
                  )}

                  {/* Megjegyzések - csak ha nem egyezik a characteristics.description-nel */}
                  {analysis?.notes && analysis.notes !== analysis?.characteristics?.description && (
                    <Alert severity="info">
                      <Typography variant="body2">{analysis.notes}</Typography>
                    </Alert>
                  )}

                  {/* AI válasz részletei debug célból */}
                  {process.env.NODE_ENV === 'development' && analysis && (
                    <Alert severity="info">
                      <Typography variant="body2">
                        <strong>AI válasz részletei (fejlesztői mód):</strong>
                      </Typography>
                      <Box component="pre" sx={{
                        backgroundColor: 'background.paper',
                        p: 1,
                        borderRadius: 1,
                        overflow: 'auto',
                        fontSize: '0.75rem',
                        mt: 1,
                        maxHeight: '200px'
                      }}>
                        {JSON.stringify(analysis, null, 2)}
                      </Box>
                    </Alert>
                  )}
                </>
              );
            }

            // Nem sikerült azonosítani
            return (
              <Alert severity="warning">
                <Typography>A növény nem azonosítható egyértelműen a feltöltött kép alapján.</Typography>
                {analysis?.notes && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {analysis.notes}
                  </Typography>
                )}
              </Alert>
            );
          })()}
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 2 }}
        >
          A növényazonosítást a PlantNet szolgáltatás végzi.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Bezárás
        </Button>
        {result.isApiError ? (
          <Button
            onClick={() => {
              // Újrapróbálkozás - új elemzés indítása
              if (onSuccess) onSuccess();
              onClose();
            }}
            variant="outlined"
            color="warning"
          >
            Újrapróbálkozás
          </Button>
        ) : (
          identified && plantExists && plant && onAddToList && (
            <Button
              onClick={() => {
                onAddToList(plant);
                if (onSuccess) onSuccess();
                onClose();
              }}
              variant="contained"
              startIcon={<AddIcon />}
            >
              Hozzáadás a listához
            </Button>
          )
        )}
      </DialogActions>
    </StyledDialog>
  );
};

export default PlantIdentificationResult;